import React from 'react';
import EmailVerificationBanner from '../components/auth/EmailVerificationBanner';
import NavBar from '../components/navigation/NavBar';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useFAB } from '../contexts/FABContext';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import LoadingScreen from '../components/common/LoadingScreen';
import FloatingActionButton, { FABActions } from '../components/common/FloatingActionButton';
import NotificationCenter from '../components/common/NotificationCenter';
import { useOrganizations } from '../hooks/useOrganizations';
const MainLayout = () => {
  const { user, loading, setActiveOrganization, signOut } = useAuth();
  const { organizations, loading: orgsLoading } = useOrganizations();

  // Ha a user dokumentumban nincs aktuális szervezet, de van szervezete (tulajdonos vagy tag),
  // állítsuk be az elsőt – ne kérjünk új onboardingot.
  const needsOrgFix = !!user && user.role !== 'customer' && !user.organizationId && organizations.length > 0;
  React.useEffect(() => {
    if (needsOrgFix && setActiveOrganization) {
      setActiveOrganization(organizations[0].id).catch(e => console.error('[MainLayout] org auto-assign failed', e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsOrgFix]);
  const { language } = useLanguage();
  const { isSidebarCollapsed } = useSidebar();
  const { isFABVisible } = useFAB();
  const location = useLocation();
  
  // Check if current page needs special layout
  const isFlowBuilder = location.pathname === '/flow-builder';
  const isFormBuilderEditor = location.pathname === '/form-builder-editor';
  const isNotesPage = location.pathname === '/notes';
  const isGanttView = location.pathname === '/projects-gantt' || 
                      location.pathname === '/project-board';
  // New modern dashboard should not have the common header top paddings/margins
  const isModernDashboard = location.pathname === '/' || 
                            location.pathname === '/dashboard' || 
                            location.pathname === '/customer-portal' ||
                            location.pathname.startsWith('/customer-portal/forms/');

  if (loading) {
    return <LoadingScreen />;
  }
  if (!user) {
    return <Navigate to="/login" />;
  }
  // Amíg nem tudjuk, van-e szervezete, ne döntsünk onboardingról
  if (!user.organizationId && user.role !== 'customer' && (orgsLoading || needsOrgFix)) {
    return <LoadingScreen />;
  }

  // Redirect to onboarding if user has no organization and hasn't completed onboarding
  // (skip for super_admin who already has org, and customer role)
  // Saját szervezet létrehozása (onboarding) kivezetve: szervezet nélkül csak a meghívó segít
  if (
    user.role !== 'super_admin' &&
    user.role !== 'customer' &&
    !user.organizationId &&
    location.pathname !== '/profile'
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-4">
        <div className="crm-card max-w-md w-full p-6 text-center space-y-3">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Nincs hozzáférésed</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">A fiókod ({user.email}) még nincs szervezethez rendelve. Kérd meg a szervezeted adminisztrátorát, hogy hívjon meg vagy adjon hozzá.</p>
          <button type="button" onClick={() => signOut?.()} className="crm-btn crm-btn-primary">Kijelentkezés</button>
        </div>
      </div>
    );
  }

  // Redirect customer role users to customer portal if they try to access dashboard
  if (user.role === 'customer' && (location.pathname === '/' || location.pathname === '/dashboard')) {
    return <Navigate to="/customer-portal" replace />;
  }

  // Get current page context for FAB actions
  const getCurrentPageActions = () => {
    const currentPath = location.pathname;
    
    // Base actions available on all pages
    const baseActions = [
      {
        ...FABActions.addCustomer,
        onClick: () => {
          window.location.href = '/customers';
        }
      },
      {
        ...FABActions.addProject,
        onClick: () => {
          window.location.href = '/projects';
        }
      },
      {
        ...FABActions.addTask,
        onClick: () => {
          window.location.href = '/tasks';
        }
      }
    ];

    // Page-specific actions
    if (currentPath === '/' || currentPath.includes('/deals')) {
      return [
        {
          ...FABActions.addDeal,
          onClick: () => {
            if (currentPath.includes('/deals')) {
              // If already on deals page, trigger form
              const event = new CustomEvent('openDealForm');
              window.dispatchEvent(event);
            } else {
              // Navigate to deals page
              window.location.href = '/deals';
            }
          }
        },
        ...baseActions
      ];
    }

    if (currentPath.includes('/customers')) {
      return [
        {
          ...FABActions.addCustomer,
          onClick: () => {
            // Trigger new customer form if on customers page
            const event = new CustomEvent('openCustomerForm');
            window.dispatchEvent(event);
          }
        },
        {
          ...FABActions.addDeal,
          onClick: () => {
            window.location.href = '/deals';
          }
        },
        {
          ...FABActions.addProject,
          onClick: () => {
            window.location.href = '/projects';
          }
        },
        {
          ...FABActions.addTask,
          onClick: () => {
            window.location.href = '/tasks';
          }
        }
      ];
    }

    if (currentPath.includes('/quotations')) {
      return [
        {
          ...FABActions.addDocument,
          label: 'Új árajánlat',
          onClick: () => {
            const event = new CustomEvent('openQuotationForm');
            window.dispatchEvent(event);
          }
        },
        ...baseActions
      ];
    }

    if (currentPath.includes('/orders')) {
      return [
        {
          ...FABActions.addDocument,
          label: 'Új megrendelés',
          onClick: () => {
            const event = new CustomEvent('openOrderForm');
            window.dispatchEvent(event);
          }
        },
        ...baseActions
      ];
    }

    if (currentPath.includes('/projects')) {
      return [
        {
          ...FABActions.addProject,
          onClick: () => {
            if (currentPath.includes('/projects')) {
              // If already on projects page, trigger form
              const event = new CustomEvent('openProjectForm');
              window.dispatchEvent(event);
            } else {
              // Navigate to projects page
              window.location.href = '/projects';
            }
          }
        },
        {
          ...FABActions.addTask,
          onClick: () => {
            window.location.href = '/tasks';
          }
        },
        {
          ...FABActions.addCustomer,
          onClick: () => {
            window.location.href = '/customers';
          }
        }
      ];
    }

    if (currentPath.includes('/tasks')) {
      return [
        {
          ...FABActions.addTask,
          onClick: () => {
            if (currentPath.includes('/tasks')) {
              // If already on tasks page, trigger form
              const event = new CustomEvent('openTaskForm');
              window.dispatchEvent(event);
            } else {
              // Navigate to tasks page
              window.location.href = '/tasks';
            }
          }
        },
        {
          ...FABActions.addProject,
          onClick: () => {
            window.location.href = '/projects';
          }
        },
        {
          ...FABActions.addCustomer,
          onClick: () => {
            window.location.href = '/customers';
          }
        }
      ];
    }

    if (currentPath.includes('/ai-quotations')) {
      // AI Quotations page has its own generate button, minimal FAB
      return [
        {
          ...FABActions.addCustomer,
          onClick: () => {
            window.location.href = '/customers';
          }
        }
      ];
    }

    if (currentPath.includes('/flow-builder')) {
      // Flow Builder page has its own node creation system, no FAB needed
      return [];
    }

    if (currentPath.includes('/form-builder-editor')) {
      // Form Builder Editor page has its own field creation system, no FAB needed
      return [];
    }

    if (currentPath.includes('/notes')) {
      // Notes page has its own note creation system, minimal FAB
      return [
        {
          ...FABActions.addCustomer,
          onClick: () => {
            window.location.href = '/customers';
          }
        }
      ];
    }

    // Default actions for other pages
    return baseActions;
  };

  return (
    <div className="flex h-screen bg-[var(--crm-bg)] overflow-hidden">
      <NavBar />

      {/* Main Content Area */}
      <div className={`flex-1 min-w-0 ${
        isModernDashboard ? 'pt-20 md:pt-0' : 'pt-20 md:pt-12'
      } flex flex-col transition-all duration-300 ${
        isSidebarCollapsed 
          ? 'md:ml-16' 
          : 'md:ml-48 lg:ml-64'
      }`}>
        {/* Content - Scrollable */}
        <main className={`flex-1 min-w-0 w-full overflow-x-hidden bg-[var(--crm-bg)] ${
          (isFlowBuilder || isFormBuilderEditor || isNotesPage)
            ? 'overflow-hidden' 
            : isGanttView
              ? 'overflow-auto'
              : isModernDashboard
                ? 'overflow-auto p-4'
                : 'overflow-auto p-3 sm:p-4 lg:p-5 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-200 dark:[&::-webkit-scrollbar-track]:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500/50'
        }`}>
          <div className="empty:hidden mb-4"><EmailVerificationBanner /></div>
          <Outlet />
        </main>
      </div>

      {/* Global Floating Action Button */}
      {getCurrentPageActions().length > 0 && isFABVisible && (
        <FloatingActionButton
          actions={getCurrentPageActions()}
          position="bottom-right"
          size="md"
        />
      )}

      {/* Notification Center Sidebar */}
      <NotificationCenter />

    </div>
  );
};

export default MainLayout; 